import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", key: "instagram", fallback: "Instagram" },
  { value: "tiktok", key: "tiktok", fallback: "TikTok" },
  { value: "youtube", key: "youtube", fallback: "YouTube" },
  { value: "x", key: "x", fallback: "X / Twitter" },
  { value: "facebook", key: "facebook", fallback: "Facebook" },
  { value: "linkedin", key: "linkedin", fallback: "LinkedIn" },
  { value: "general", key: "general", fallback: "General" },
];

const toneOptions = [
  { value: "natural", key: "natural", fallback: "Natural" },
  { value: "casual", key: "casual", fallback: "Casual" },
  { value: "confident", key: "confident", fallback: "Confident" },
  { value: "sales", key: "sales", fallback: "Sales" },
  { value: "storytelling", key: "storytelling", fallback: "Storytelling" },
  { value: "professional", key: "professional", fallback: "Professional" },
];

const formatOptions = [
  { value: "caption", key: "caption", fallback: "Caption" },
  { value: "hook", key: "hook", fallback: "Hook" },
  { value: "short_script", key: "shortScript", fallback: "Short Script" },
  { value: "ad_copy", key: "adCopy", fallback: "Ad Copy" },
  { value: "carousel_copy", key: "carouselCopy", fallback: "Carousel Copy" },
  { value: "general", key: "general", fallback: "General Rewrite" },
];

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return (
            item.text ||
            item.content ||
            item.label ||
            item.title ||
            item.name ||
            JSON.stringify(item)
          );
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
      .filter(Boolean);
  }

  return [];
};

const formatResultToText = (result, t) => {
  if (!result) return "";
  if (typeof result === "string") return result;

  const sections = [];

  const rewritten =
    result.rewritten_text ||
    result.rewrite ||
    result.output ||
    result.content ||
    result.result ||
    result.text;

  if (rewritten) {
    sections.push(`${t("studioTools.nigerianAudienceRewriteTool.exportLabels.rewrittenOutput")}:\n${rewritten}`);
  }

  const summary =
    result.summary || result.notes || result.overview || result.reasoning || result.explanation;

  if (summary) {
    sections.push(`${t("studioTools.nigerianAudienceRewriteTool.exportLabels.summary")}:\n${summary}`);
  }

  const styleNotes = normalizeArray(
    result.style_notes || result.localization_notes || result.adaptation_notes
  );
  if (styleNotes.length) {
    sections.push(
      `${t("studioTools.nigerianAudienceRewriteTool.exportLabels.adaptationNotes")}:\n${styleNotes
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const alternatives = normalizeArray(
    result.alternatives || result.variations || result.options || result.more_versions
  );
  if (alternatives.length) {
    sections.push(
      `${t("studioTools.nigerianAudienceRewriteTool.exportLabels.alternativeVersions")}:\n${alternatives
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const suggestions = normalizeArray(result.suggestions || result.tips || result.recommendations);
  if (suggestions.length) {
    sections.push(
      `${t("studioTools.nigerianAudienceRewriteTool.exportLabels.suggestions")}:\n${suggestions
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  if (!sections.length) {
    return JSON.stringify(result, null, 2);
  }

  return sections.join("\n\n");
};

const NigerianAudienceRewriteTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();
  const [theme, setTheme] = useState(getStoredTheme());
  const [originalText, setOriginalText] = useState("");
  const [tone, setTone] = useState("natural");
  const [contentFormat, setContentFormat] = useState("general");
  const [brandContext, setBrandContext] = useState("");
  const [goal, setGoal] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedPlatform = sharedPlatform || "general";
  const topicValue = sharedTopic || "";

  useEffect(() => {
    const syncTheme = () => setTheme(getStoredTheme());

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const styles = useMemo(() => {
    return {
      wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
      card: {
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.22)"
          : "0 10px 30px rgba(15,23,42,0.06)",
      },
      headerRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
      },
      titleWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 780,
      },
      badge: {
        alignSelf: "flex-start",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(234,179,8,0.16)" : "rgba(234,179,8,0.10)",
        color: isDark ? "#fde68a" : "#92400e",
        border: `1px solid ${isDark ? "rgba(234,179,8,0.24)" : "rgba(217,119,6,0.18)"}`,
      },
      title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      subtitle: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.6,
        color: isDark ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginTop: 18,
      },
      fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
      },
      fullWidthField: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: "1 / -1",
      },
      label: {
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      input: {
        width: "100%",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "13px 14px",
        outline: "none",
        fontSize: 14,
        boxSizing: "border-box",
      },
      textarea: {
        width: "100%",
        minHeight: 140,
        resize: "vertical",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: 14,
        outline: "none",
        fontSize: 14,
        lineHeight: 1.55,
        boxSizing: "border-box",
      },
      actionsRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 18,
      },
      primaryButton: {
        border: "none",
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        background: loading
          ? isDark
            ? "rgba(234,179,8,0.45)"
            : "rgba(217,119,6,0.45)"
          : isDark
          ? "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)"
          : "linear-gradient(135deg, #d97706 0%, #ea580c 100%)",
        color: "#ffffff",
        boxShadow: loading
          ? "none"
          : isDark
          ? "0 12px 24px rgba(245,158,11,0.20)"
          : "0 12px 24px rgba(217,119,6,0.16)",
      },
      secondaryButton: {
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(30,41,59,0.82)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      mutedText: {
        fontSize: 13,
        color: isDark ? "rgba(226,232,240,0.68)" : "rgba(15,23,42,0.62)",
      },
      errorBox: {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(127,29,29,0.25)" : "rgba(254,226,226,1)",
        border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(248,113,113,0.28)"}`,
        color: isDark ? "#fecaca" : "#991b1b",
        fontSize: 14,
        lineHeight: 1.5,
      },
      resultCard: {
        background: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 18,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      },
      resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },
      resultTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 800,
      },
      resultActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
      },
      section: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
      },
      sectionTitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 800,
        color: isDark ? "#f1f5f9" : "#0f172a",
      },
      paragraph: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.75,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        whiteSpace: "pre-wrap",
      },
      list: {
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        fontSize: 14,
        lineHeight: 1.65,
      },
      chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
      },
      chip: {
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(51,65,85,0.88)" : "#ffffff",
        color: isDark ? "#cbd5e1" : "#334155",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      },
      outputBox: {
        borderRadius: 16,
        padding: 16,
        background: isDark ? "rgba(15,23,42,0.85)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      },
    };
  }, [isDark, loading]);

  const formattedText = useMemo(() => formatResultToText(result, t), [result, t]);

  const rewrittenText =
    result?.rewritten_text ||
    result?.rewrite ||
    result?.output ||
    result?.content ||
    result?.result ||
    result?.text ||
    "";

  const adaptationNotes = useMemo(
    () =>
      normalizeArray(
        result?.style_notes || result?.localization_notes || result?.adaptation_notes
      ),
    [result]
  );

  const alternatives = useMemo(
    () =>
      normalizeArray(
        result?.alternatives || result?.variations || result?.options || result?.more_versions
      ),
    [result]
  );

  const suggestions = useMemo(
    () => normalizeArray(result?.suggestions || result?.tips || result?.recommendations),
    [result]
  );

  const clearResults = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const resetAll = () => {
    setOriginalText("");
    setTone("natural");
    setContentFormat("general");
    setBrandContext("");
    setGoal("");
    setResult(null);
    setCopied(false);
    setError("");
    if (typeof setSharedTopic === "function") setSharedTopic("");
    if (typeof setSharedPlatform === "function") setSharedPlatform("general");
  };

  const copyResult = async () => {
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (copyError) {
      setError(t("studioTools.nigerianAudienceRewriteTool.messages.copyError"));
    }
  };

  const tryPost = async (paths, payload) => {
    let lastError = null;

    for (const path of paths) {
      try {
        const response = await API.post(path, payload);
        return response?.data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError;
  };

  const handleRewrite = async () => {
    if (!originalText.trim()) {
      setError(t("studioTools.nigerianAudienceRewriteTool.messages.missingOriginalText"));
      return;
    }

    setLoading(true);
    setError("");
    setCopied(false);

    const payload = {
      text: originalText.trim(),
      topic: topicValue.trim(),
      platform: selectedPlatform,
      tone,
      format: contentFormat,
      brand_context: brandContext.trim(),
      goal: goal.trim(),
    };

    try {
      const data = await tryPost(
        [
          "/studio/nigerian-rewrite",
          "/studio/nigerian_rewrite",
          "/studio/nigerian-audience-rewrite",
          "/studio/nigerian_audience_rewrite",
        ],
        payload
      );

      const normalized =
        data?.result ||
        data?.rewrite ||
        data?.data ||
        data?.nigerian_rewrite ||
        data;

      setResult(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t("studioTools.nigerianAudienceRewriteTool.messages.genericError");
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const platformLabel = (value) =>
    t(`studioTools.nigerianAudienceRewriteTool.platformOptions.${platformOptions.find((item) => item.value === value)?.key}`) ||
    platformOptions.find((item) => item.value === value)?.fallback ||
    "General";

  const toneLabel = (value) =>
    t(`studioTools.nigerianAudienceRewriteTool.toneOptions.${toneOptions.find((item) => item.value === value)?.key}`) ||
    toneOptions.find((item) => item.value === value)?.fallback ||
    "Natural";

  const formatLabel = (value) =>
    t(`studioTools.nigerianAudienceRewriteTool.formatOptions.${formatOptions.find((item) => item.value === value)?.key}`) ||
    formatOptions.find((item) => item.value === value)?.fallback ||
    "General Rewrite";

  return (
    <MotionSurface>
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.badge}>{t("studioTools.nigerianAudienceRewriteTool.badge")}</span>
            <h2 style={styles.title}>{t("studioTools.nigerianAudienceRewriteTool.title")}</h2>
            <p style={styles.subtitle}>{t("studioTools.nigerianAudienceRewriteTool.subtitle")}</p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.nigerianAudienceRewriteTool.fields.topic.label")}</label>
            <input
              type="text"
              value={topicValue}
              onChange={(e) => setSharedTopic?.(e.target.value)}
              placeholder={t("studioTools.nigerianAudienceRewriteTool.fields.topic.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.nigerianAudienceRewriteTool.fields.topic.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.nigerianAudienceRewriteTool.fields.platform.label")}</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSharedPlatform?.(e.target.value)}
              style={styles.input}
            >
              {platformOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.nigerianAudienceRewriteTool.platformOptions.${option.key}`) || option.fallback}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.nigerianAudienceRewriteTool.fields.platform.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.nigerianAudienceRewriteTool.fields.tone.label")}</label>
            <select value={tone} onChange={(e) => setTone(e.target.value)} style={styles.input}>
              {toneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.nigerianAudienceRewriteTool.toneOptions.${option.key}`) || option.fallback}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.nigerianAudienceRewriteTool.fields.tone.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.nigerianAudienceRewriteTool.fields.contentFormat.label")}</label>
            <select
              value={contentFormat}
              onChange={(e) => setContentFormat(e.target.value)}
              style={styles.input}
            >
              {formatOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.nigerianAudienceRewriteTool.formatOptions.${option.key}`) || option.fallback}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.nigerianAudienceRewriteTool.fields.contentFormat.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.nigerianAudienceRewriteTool.fields.goal.label")}</label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t("studioTools.nigerianAudienceRewriteTool.fields.goal.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.nigerianAudienceRewriteTool.fields.goal.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>{t("studioTools.nigerianAudienceRewriteTool.fields.brandContext.label")}</label>
            <textarea
              value={brandContext}
              onChange={(e) => setBrandContext(e.target.value)}
              placeholder={t("studioTools.nigerianAudienceRewriteTool.fields.brandContext.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.nigerianAudienceRewriteTool.fields.brandContext.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>{t("studioTools.nigerianAudienceRewriteTool.fields.originalContent.label")}</label>
            <textarea
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              placeholder={t("studioTools.nigerianAudienceRewriteTool.fields.originalContent.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.nigerianAudienceRewriteTool.fields.originalContent.hint")} />
          </div>
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleRewrite}
            disabled={loading}
          >
            {loading
              ? t("studioTools.nigerianAudienceRewriteTool.buttons.rewriting")
              : t("studioTools.nigerianAudienceRewriteTool.buttons.rewrite")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={clearResults}
          >
            {t("studioTools.nigerianAudienceRewriteTool.buttons.clearResults")}
          </UtilityButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={resetAll}
          >
            {t("studioTools.nigerianAudienceRewriteTool.buttons.reset")}
          </UtilityButton>
        </ActionRow>

        <div style={{ marginTop: 10 }}>
          <span style={styles.mutedText}>
            {t("studioTools.nigerianAudienceRewriteTool.tip")}
          </span>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={isDark} count={3} />
      ) : result ? (
        <div style={styles.card}>
          <ResultCard
            colors={{
              border: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)",
              cardBgStrong: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
              shadowSoft: isDark
                ? "0 10px 24px rgba(2,6,23,0.22)"
                : "0 6px 18px rgba(15,23,42,0.05)",
            }}
            index={0}
          >
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>{t("studioTools.nigerianAudienceRewriteTool.result.title")}</h3>

              <ActionRow style={styles.resultActions}>
                <CopyButton
                  type="button"
                  copied={copied}
                  onClick={copyResult}
                >
                  {copied
                    ? t("studioTools.nigerianAudienceRewriteTool.buttons.copied")
                    : t("studioTools.nigerianAudienceRewriteTool.buttons.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="nigerian_rewrite"
                  title={`${t("studioTools.nigerianAudienceRewriteTool.result.assetTitle")}${topicValue ? ` - ${topicValue}` : ""}`}
                  topic={topicValue || t("studioTools.nigerianAudienceRewriteTool.result.assetTitle")}
                  platform={selectedPlatform}
                  content={formattedText}
                  meta={{
                    originalText,
                    tone,
                    contentFormat,
                    brandContext,
                    goal,
                  }}
                />
              </ActionRow>
            </div>

            <div style={styles.chipRow}>
              <span style={styles.chip}>{platformLabel(selectedPlatform)}</span>
              <span style={styles.chip}>{formatLabel(contentFormat)}</span>
              <span style={styles.chip}>{toneLabel(tone)}</span>
              {topicValue ? <span style={styles.chip}>{topicValue}</span> : null}
            </div>

            {rewrittenText ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.nigerianAudienceRewriteTool.result.sections.rewrittenOutput")}</h4>
                <div style={styles.outputBox}>
                  <p style={styles.paragraph}>{rewrittenText}</p>
                </div>
              </div>
            ) : null}

            {result?.summary ||
            result?.notes ||
            result?.overview ||
            result?.reasoning ||
            result?.explanation ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.nigerianAudienceRewriteTool.result.sections.summary")}</h4>
                <p style={styles.paragraph}>
                  {result?.summary ||
                    result?.notes ||
                    result?.overview ||
                    result?.reasoning ||
                    result?.explanation}
                </p>
              </div>
            ) : null}

            {adaptationNotes.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.nigerianAudienceRewriteTool.result.sections.adaptationNotes")}</h4>
                <ul style={styles.list}>
                  {adaptationNotes.map((item, index) => (
                    <li key={`note-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {alternatives.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.nigerianAudienceRewriteTool.result.sections.alternativeVersions")}</h4>
                <ul style={styles.list}>
                  {alternatives.map((item, index) => (
                    <li key={`alternative-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {suggestions.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.nigerianAudienceRewriteTool.result.sections.suggestions")}</h4>
                <ul style={styles.list}>
                  {suggestions.map((item, index) => (
                    <li key={`suggestion-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!formattedText ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.nigerianAudienceRewriteTool.result.sections.rawResult")}</h4>
                <p style={styles.paragraph}>{JSON.stringify(result, null, 2)}</p>
              </div>
            ) : null}
          </ResultCard>
        </div>
      ) : null}
    </div>
    </MotionSurface>
  );
};

export default NigerianAudienceRewriteTool;